import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Textarea } from './textarea';
import { Pencil } from 'lucide-react';

interface TextEditModalProps {
  value: string;
  onChange: (value: string) => void;
  title?: string;
  placeholder?: string;
  /** Minimum character count to show the edit button (default: 100) */
  minLengthForButton?: number;
  /** Always show the button regardless of text length */
  alwaysShowButton?: boolean;
  /** Custom trigger element (optional) */
  trigger?: React.ReactNode;
  /** Additional class for the trigger button */
  buttonClassName?: string;
}

/**
 * Modal editor for long text content
 * Shows a pencil button (📝) for texts longer than minLengthForButton
 * Opens a full-screen modal with a large textarea for comfortable editing
 */
export function TextEditModal({
  value,
  onChange,
  title = 'Text bearbeiten',
  placeholder = 'Text eingeben...',
  minLengthForButton = 100,
  alwaysShowButton = false,
  trigger,
  buttonClassName = '',
}: TextEditModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editValue, setEditValue] = useState(value);

  // Sync edit value when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditValue(value);
    }
  }, [isOpen, value]);

  const handleSave = () => {
    onChange(editValue);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setEditValue(value); // Reset to original
    setIsOpen(false);
  };

  // Don't show button if text is short enough (unless alwaysShowButton is true)
  const shouldShowButton = alwaysShowButton || (value && value.length > minLengthForButton);

  if (!shouldShowButton && !trigger) {
    return null;
  }

  return (
    <>
      {/* Trigger button */}
      {trigger ? (
        <span onClick={() => setIsOpen(true)} className="cursor-pointer">
          {trigger}
        </span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className={`h-6 w-6 p-0 text-gray-400 hover:text-blue-600 hover:bg-blue-50 ${buttonClassName}`}
          title="Text in Vollansicht bearbeiten"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}

      {/* Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 py-4">
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={placeholder}
              className="w-full h-[400px] resize-none font-mono text-sm leading-relaxed"
              autoFocus
            />
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <div className="text-xs text-gray-500 mr-auto">
              {editValue.length} Zeichen
            </div>
            <Button variant="outline" onClick={handleCancel}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Inline text field with optional modal editor button for long texts
 * Combines a regular textarea with a modal editor button
 */
interface TextFieldWithModalProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  minLengthForModal?: number;
  modalTitle?: string;
}

export function TextFieldWithModal({
  value,
  onChange,
  placeholder = 'Beschreibung',
  className = '',
  rows = 1,
  minLengthForModal = 100,
  modalTitle = 'Beschreibung bearbeiten',
}: TextFieldWithModalProps) {
  const showModalButton = value && value.length > minLengthForModal;

  return (
    <div className="relative flex items-start gap-1">
      <Textarea
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value);
          // Auto-expand textarea
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        onFocus={(e) => {
          // Expand on focus
          e.target.style.height = 'auto';
          e.target.style.height = e.target.scrollHeight + 'px';
        }}
        className={`min-h-[28px] text-sm border-gray-200 focus:border-blue-400 resize-none overflow-hidden ${className}`}
        placeholder={placeholder}
        rows={rows}
      />
      {showModalButton && (
        <TextEditModal
          value={value}
          onChange={onChange}
          title={modalTitle}
          placeholder={placeholder}
          alwaysShowButton={true}
          buttonClassName="flex-shrink-0 mt-0.5"
        />
      )}
    </div>
  );
}
